/**
 * /privacidad — Política de Privacidad.
 *
 * Required by Culqi onboarding (KYC) and the Peruvian Personal Data
 * Protection Law (Ley 29733). Lists what we collect, what we do with it,
 * who we share it with (Culqi only), and the user's rights.
 */

import { LegalSection, LegalShell } from '@/components/layout/LegalShell';
import { contact } from '@/lib/contact';

export function PrivacyPage() {
  return (
    <LegalShell eyebrow="Legal" title="Política de privacidad" updated="Última actualización: mayo de 2026">
      <LegalSection title="1. Responsable del tratamiento">
        <p>
          El responsable del tratamiento de tus datos personales es{' '}
          <strong>{contact.legalName}</strong>
          {contact.legalRuc && (
            <>
              {' '}(RUC <span className="tabular">{contact.legalRuc}</span>)
            </>
          )}
          , con operación en {contact.legalAddress}.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            Correo del responsable:{' '}
            <a
              href={`mailto:${contact.privacyEmail}`}
              className="underline-offset-2 hover:underline"
              style={{ color: 'var(--accent)' }}
            >
              {contact.privacyEmail}
            </a>
          </li>
          <li>
            Teléfono / WhatsApp:{' '}
            <a
              href={`tel:${contact.phoneTel}`}
              className="underline-offset-2 hover:underline"
              style={{ color: 'var(--accent)' }}
            >
              {contact.phoneDisplay}
            </a>
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="2. Datos que recopilamos">
        <ul className="list-disc space-y-1 pl-5">
          <li><strong>De registro:</strong> nombre completo, correo electrónico, contraseña (hashed).</li>
          <li><strong>De uso:</strong> cálculos guardados, exportaciones realizadas, fecha y hora de acceso.</li>
          <li><strong>De facturación:</strong> identificador del cargo de Culqi (no almacenamos datos de tarjeta).</li>
          <li><strong>Técnicos:</strong> dirección IP y user-agent del navegador, para auditoría y seguridad.</li>
        </ul>
      </LegalSection>

      <LegalSection title="3. Finalidad del tratamiento">
        <p>
          Usamos tus datos exclusivamente para: (a) proveer el servicio que contrataste, (b)
          procesar tus pagos a través de Culqi, (c) enviarte comunicaciones operativas (recibos,
          cambios de servicio), (d) detectar fraudes y proteger la integridad de la plataforma.
        </p>
        <p>
          <strong>No usamos tus datos para marketing</strong> sin tu consentimiento expreso. No
          vendemos ni cedemos información personal a terceros con fines publicitarios.
        </p>
      </LegalSection>

      <LegalSection title="4. Encargados y transferencias">
        <p>Compartimos datos únicamente con los siguientes encargados:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Culqi S.A.C.</strong> (Perú) — procesa tus pagos. Le entregamos tu correo y el
            monto a cobrar; nunca recibimos los datos de tarjeta. Política de Culqi:{' '}
            <a href="https://www.culqi.com/privacidad" target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline" style={{ color: 'var(--accent)' }}>
              culqi.com/privacidad
            </a>
            .
          </li>
          <li>
            <strong>Proveedor de hosting</strong> (servidores en infraestructura cloud). Los datos
            permanecen en servidores ubicados en la región.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Plazo de conservación">
        <p>
          Conservamos tus datos mientras tu cuenta esté activa. Si solicitas la eliminación,
          borraremos tu información personal en un máximo de 30 días, salvo aquella que estemos
          obligados a conservar por ley (registros contables: 5 años; libro de reclamaciones: 2
          años).
        </p>
      </LegalSection>

      <LegalSection title="6. Tus derechos (ARCO)">
        <p>
          De acuerdo con la <strong>Ley 29733</strong>, tienes derecho a:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li><strong>Acceso</strong> — saber qué datos tuyos tenemos.</li>
          <li><strong>Rectificación</strong> — corregir datos inexactos.</li>
          <li><strong>Cancelación</strong> — eliminar tus datos.</li>
          <li><strong>Oposición</strong> — oponerte a un uso específico.</li>
          <li><strong>Portabilidad</strong> — recibir tus datos en formato estructurado.</li>
        </ul>
        <p>
          Para ejercer cualquiera de estos derechos escríbenos a{' '}
          <a
            href={`mailto:${contact.privacyEmail}`}
            className="underline-offset-2 hover:underline"
            style={{ color: 'var(--accent)' }}
          >
            {contact.privacyEmail}
          </a>{' '}
          desde el correo asociado a tu cuenta. Responderemos en máximo 20 días hábiles.
        </p>
      </LegalSection>

      <LegalSection title="7. Seguridad">
        <p>
          Aplicamos medidas técnicas y organizativas razonables para proteger tus datos: contraseñas
          almacenadas con hashing bcrypt, cifrado TLS en tránsito, control de acceso por roles, y
          registros de auditoría de todas las operaciones administrativas.
        </p>
      </LegalSection>

      <LegalSection title="8. Menores de edad">
        <p>
          El servicio está dirigido a profesionales y estudiantes mayores de 18 años. No recopilamos
          deliberadamente datos de menores. Si detectamos una cuenta de un menor sin autorización
          parental, la eliminaremos.
        </p>
      </LegalSection>

      <LegalSection title="9. Cambios en esta política">
        <p>
          Si modificamos esta política te avisaremos por correo con al menos 15 días de
          anticipación. La fecha de "última actualización" siempre refleja la versión vigente.
        </p>
      </LegalSection>
    </LegalShell>
  );
}
